import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import api from '../services/api';
import { toast } from 'react-toastify';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const fetchBill = (id) => api.get(`/bills/${id}`).then(res => res.data.data.bill);

export default function BillEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form, setForm] = useState({
        consumerNumber: '',
        consumerName: '',
        dueDate: '',
        feeBeforeDueDate: '',
        feeAfterDueDate: '',
        billingMonth: '',
        description: ''
    });

    // Customer link and search states
    const [consumerQuery, setConsumerQuery] = useState('');
    const [showCustomersDropdown, setShowCustomersDropdown] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customersList, setCustomersList] = useState([]);
    const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
    const dropdownRef = useRef(null);

    // Modal state for adding a customer
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [newCustomerForm, setNewCustomerForm] = useState({
        consumerNumber: '',
        name: '',
        email: '',
        mobile: '',
        address: ''
    });

    // Fetch existing bill data
    const { data: bill, isLoading: isFetching } = useQuery(['bill', id], () => fetchBill(id), {
        onSuccess: (data) => {
            // pre-fill the form
            if (data.status === 'paid') {
                toast.error('Cannot edit a paid bill');
                navigate('/bills');
                return;
            }
            setForm({
                consumerNumber: data.customer?.consumerNumber || '',
                consumerName: data.consumerName || '',
                dueDate: data.dueDate ? Array.isArray(data.dueDate) ? data.dueDate[0] : typeof data.dueDate === 'string' ? data.dueDate.split('T')[0] : '' : '',
                feeBeforeDueDate: data.feeBeforeDueDate || '',
                feeAfterDueDate: data.feeAfterDueDate || '',
                billingMonth: data.billingMonth || '',
                description: data.description || ''
            });
        }
    });

    // Mutation to update bill
    const updateBill = useMutation(
        (data) => api.put(`/bills/${id}`, data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['bill', id]);
                queryClient.invalidateQueries('bills');
                toast.success('Bill updated successfully');
                navigate('/bills');
            },
            onError: (err) => {
                toast.error(err.response?.data?.message || 'Failed to update bill');
            }
        }
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.consumerName || !form.dueDate || !form.feeBeforeDueDate || !form.feeAfterDueDate) {
            toast.error('Please fill in all required fields');
            return;
        }
        updateBill.mutate({
            ...form,
            feeBeforeDueDate: parseFloat(form.feeBeforeDueDate),
            feeAfterDueDate: parseFloat(form.feeAfterDueDate)
        });
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    if (isFetching) return <div className="text-center py-8">Loading...</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <button onClick={() => navigate('/bills')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                    <ArrowLeftIcon className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Edit Bill</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Update challan parameters</p>
                </div>
            </div>

            <div className="card max-w-2xl">
                <div className="card-header">
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white">Challan Parameters</h3>
                </div>
                <form onSubmit={handleSubmit} className="card-body space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="relative">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Consumer Number
                            </label>
                            <input
                                type="text"
                                value={form.consumerNumber || ''}
                                className="input-field bg-slate-50 dark:bg-slate-800/50 cursor-not-allowed opacity-80"
                                disabled
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Consumer Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="consumerName"
                                value={form.consumerName || ''}
                                onChange={handleChange}
                                placeholder="e.g. Jane Smith"
                                className="input-field"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Amount Before Due Date (PKR) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="feeBeforeDueDate"
                                value={form.feeBeforeDueDate}
                                onChange={handleChange}
                                placeholder="e.g. 5000"
                                className="input-field"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Amount After Due Date (PKR) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="feeAfterDueDate"
                                value={form.feeAfterDueDate}
                                onChange={handleChange}
                                placeholder="e.g. 5500"
                                className="input-field"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Due Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="dueDate"
                                value={form.dueDate}
                                onChange={handleChange}
                                className="input-field"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Billing Month (Optional)
                            </label>
                            <input
                                type="month"
                                name="billingMonth"
                                value={form.billingMonth}
                                onChange={handleChange}
                                className="input-field"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Description / Notes
                        </label>
                        <textarea
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            placeholder="Internal record details"
                            rows="3"
                            className="input-field"
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                        <button
                            type="button"
                            onClick={() => navigate('/bills')}
                            className="btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={updateBill.isLoading}
                            className="btn-primary"
                        >
                            {updateBill.isLoading ? 'Updating...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
